import React, {useEffect, useMemo, useState} from 'react';
import {
  IFdCrop,
  IFdProductionHistory,
  ISupplierUserProfile,
} from 'constants/interfaces';
import {
  reduxToggleSidePanel,
  reduxSetSidePanelEditState,
} from 'appredux/actions';
import {apiGetFdCrops, apiUpdateFdProductionHistory} from 'constants/Api';
import {
  FdProductionHistoryForm,
  Submission,
} from 'components/applications/FdProductionHistoryForm';

const AddOrUpdateFdProductionHistory = (props: {
  user: ISupplierUserProfile;
  cropId: string;
  applicationId: number;
  farmId: number;
  allHistory: IFdProductionHistory[];
}) => {
  const [crops, setCrops] = useState<IFdCrop[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [hasSuccessfullyUpdated, setHasSuccessfullyUpdated] = useState(false);

  useEffect(() => {
    // Signal edit started
    reduxSetSidePanelEditState('editing');

    setIsLoading(true);
    apiGetFdCrops()
      .then((crops) => setCrops(crops))
      .catch((err) => console.error('failed to load crops', err))
      .finally(() => setIsLoading(false));

    return () => {
      // reset when panel unmounts if not saved
      reduxSetSidePanelEditState('idle');
    };
  }, []);

  const handleCancel = () => reduxToggleSidePanel(false, null);

  const handleSubmit = (submission: Partial<Submission>[]) => {
    setIsLoading(true);
    Promise.all(
      submission.map((item) =>
        apiUpdateFdProductionHistory({
          id: item.id ? Number(item.id) : undefined,
          cropId: parseInt(item.cropId!),
          numberOfHectaresFarmed: item.numberOfHectaresFarmed!,
          yieldPerHectare: item.yieldPerHectare!,
          year: item.year!,
          farmId: item.farmId || props.farmId,
          applicationId: props.applicationId,
          notes: item.notes ?? '',
        }),
      ),
    )
      .then(() => {
        setHasSuccessfullyUpdated(true);
        // Immediately signal that a save has occurred to trigger data refresh
        reduxSetSidePanelEditState('saved');
      })
      .catch((err) => {
        alert(
          'Failed to update production history at the moment. Please try again later',
        );
        console.error('Failed to update production history', err);
      })
      .finally(() => setIsLoading(false));
  };

  const canWrite = true;

  const formData = useMemo(() => {
    const formData: Partial<Submission>[] = [];
    const prevYears = [2024, 2023, 2022];
    for (const prevYear of prevYears) {
      const existing = props.allHistory.find(
        (item) => item.year === prevYear && item.crop?.id === props.cropId,
      );
      if (existing) {
        const numberOfHectaresFarmed =
          existing.numberOfHectaresFarmed ?? undefined;
        const yieldPerHectare = existing.yieldPerHectare ?? undefined;
        const year = existing.year ?? undefined;
        const notes = existing.notes ?? undefined;

        formData.push({
          id: existing.id,
          cropId: existing.crop?.id,
          numberOfHectaresFarmed,
          yieldPerHectare,
          year,
          farmId: existing.farm?.id ? parseInt(existing.farm.id) : undefined,
          notes,
        });
      } else {
        formData.push({year: prevYear, cropId: props.cropId});
      }
    }
    return formData;
  }, [props.cropId, props.allHistory]);

  // Close panel after successful update
  useEffect(() => {
    if (hasSuccessfullyUpdated) {
      // Close the side panel after a short delay to allow data refresh
      setTimeout(() => {
        reduxToggleSidePanel(false, null);
      }, 100);
    }
  }, [hasSuccessfullyUpdated]);

  if (hasSuccessfullyUpdated) return null;

  return (
    <FdProductionHistoryForm
      defaultValues={formData}
      allCrops={crops}
      isLoading={isLoading}
      submitButtonTitle="Update"
      onSubmit={handleSubmit}
      cancelButtonTitle="Cancel"
      onCancel={handleCancel}
      readonly={!canWrite}
    />
  );
};

export default AddOrUpdateFdProductionHistory;
